import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { TagIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { resolveStatusRank } from "@/constants/statuses";
import { useCreatorsTableContext } from "../CreatorsTableContext";
import { StatusFilterProps } from "../types";

/** One checklist row: a label and every raw status value that resolves to it. */
interface StatusOption {
  label: string;
  values: string[];
  count: number;
  /** Earliest place in the campaign flow any of its values sits (resolveStatusRank). */
  rank: number;
}

export default function StatusFilter({ id }: StatusFilterProps) {
  const { table, serverFacets, statusLabel, campaigns } = useCreatorsTableContext();

  // Option list + counts: the loaded rows in a campaign table, the org-wide
  // facets in the Global CRM (one page can't enumerate every status).
  const pageFacets = table.getColumn("status")?.getFacetedUniqueValues();
  const statusCounts = useMemo<Map<string, number>>(() => {
    if (serverFacets) return new Map(Object.entries(serverFacets.status));
    return new Map((pageFacets ?? new Map()) as Map<string, number>);
  }, [serverFacets, pageFacets]);

  // Grouped by DISPLAY label: a legacy stage is a per-campaign UUID, so
  // org-wide the same "Received Flowers" stage is a dozen different values.
  // One row per label, counts summed, and toggling it toggles every value
  // behind it (the filter itself still carries the raw values). Listed in
  // campaign-flow order, not alphabetically.
  const options = useMemo<StatusOption[]>(() => {
    const byLabel = new Map<string, StatusOption>();
    for (const [value, count] of statusCounts) {
      if (value === "") continue;
      const label = statusLabel(value);
      const option = byLabel.get(label) ?? { label, values: [], count: 0, rank: Infinity };
      option.values.push(value);
      option.count += count;
      option.rank = Math.min(option.rank, resolveStatusRank(value, campaigns));
      byLabel.set(label, option);
    }
    return Array.from(byLabel.values()).sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label));
  }, [statusCounts, statusLabel, campaigns]);

  const selectedStatuses = useMemo(() => {
    const filterValue = table.getColumn("status")?.getFilterValue() as string[];
    return filterValue ?? [];
  }, [table.getColumn("status")?.getFilterValue()]);

  const isChecked = (option: StatusOption) =>
    option.values.every((value) => selectedStatuses.includes(value));
  const checkedCount = options.filter(isChecked).length;

  const handleStatusFilter = (checked: boolean, option: StatusOption) => {
    const current = (table.getColumn("status")?.getFilterValue() as string[] | undefined) ?? [];
    const next = checked
      ? [...current, ...option.values.filter((value) => !current.includes(value))]
      : current.filter((value) => !option.values.includes(value));

    table.getColumn("status")?.setFilterValue(next.length ? next : undefined);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" className="h-7.5 px-2 hover:bg-muted-foreground/10 data-[state=open]:bg-muted data-[state=open]:[&>svg]:opacity-100">
          <TagIcon
            className="opacity-60"
            size={16}
            aria-hidden="true"
          />
          Status
          {checkedCount > 0 && (
            <Badge variant="count" className="-me-1">
              {checkedCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto min-w-36 p-1"
        align="start"
      >
        <div className="space-y-2">
          <div className="text-muted-foreground text-xs font-medium p-2 pb-0">
            Filters
          </div>
          <div className="max-h-96 overflow-y-auto [scrollbar-color:--alpha(var(--foreground)/30%)_transparent]">
            {options.map((option, i) => (
              <div
                key={option.label}
                className="flex items-center gap-2 p-2 pr-3 hover:bg-muted rounded-sm cursor-pointer"
                onClick={() => handleStatusFilter(!isChecked(option), option)}
              >
                <Checkbox
                  id={`${id}-${i}`}
                  checked={isChecked(option)}
                  onCheckedChange={(checked: boolean) =>
                    handleStatusFilter(checked, option)
                  }
                />
                <Label className="flex grow justify-between gap-2 font-normal cursor-pointer text-xs">
                  {option.label}{" "}
                  <span className="text-muted-foreground ms-2">
                    {option.count}
                  </span>
                </Label>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
